import * as Dialog from '@radix-ui/react-dialog';
import { CheckCircle2, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { ProvisioningJobItem } from '@control-center/shared';
import { formatBRDateTime, markProvisionedRequestSchema } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { Field, Input, Textarea } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';
import { extractErrorMessage } from '../../lib/errors';
import { useMarkProvisioned } from '../../hooks/use-provisioning';

interface ProvisioningJobCardProps {
  job: ProvisioningJobItem;
  canMarkProvisioned: boolean;
}

export function ProvisioningJobCard({
  job,
  canMarkProvisioned,
}: ProvisioningJobCardProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    external_tenant_id: '',
    external_user_id: '',
    access_url: '',
    notes: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const mut = useMarkProvisioned();

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrors({});
    const payload = {
      external_tenant_id: form.external_tenant_id.trim(),
      external_user_id: form.external_user_id.trim() || null,
      access_url: form.access_url.trim() || null,
      notes: form.notes.trim() || null,
    };
    const parsed = markProvisionedRequestSchema.safeParse(payload);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path[0]?.toString() ?? '_';
        next[path] = issue.message;
      }
      setErrors(next);
      return;
    }
    try {
      await mut.mutateAsync({ jobId: job.id, body: parsed.data });
      toast.success(`Provisionado: ${job.subscriptionEntitlement.saasProduct.name}`);
      setOpen(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao marcar como provisionado.'));
    }
  }

  return (
    <div className="flex items-center gap-3 rounded-[8px] border border-line bg-panel-2 p-3 text-sm">
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-ink">
          {job.subscriptionEntitlement.saasProduct.name} →{' '}
          {job.subscriptionEntitlement.externalPlanName}
        </div>
        <code className="text-[11px] text-muted-2">
          {job.subscriptionEntitlement.saasProduct.slug}/
          {job.subscriptionEntitlement.externalPlanSlug}
        </code>
        <div className="mt-1 text-[11px] text-muted-3">
          Solicitado em {formatBRDateTime(job.createdAt)}
          {job.attemptsCount > 0 && ` · ${job.attemptsCount} tentativa(s)`}
        </div>
        {job.errorMessage && (
          <div className="mt-1 max-w-prose truncate text-[11px] text-bad">{job.errorMessage}</div>
        )}
      </div>
      {canMarkProvisioned && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex h-7 shrink-0 items-center gap-1 rounded-[6px] border border-line bg-panel-3 px-2 text-[12px] text-ink hover:bg-panel"
        >
          <CheckCircle2 size={13} strokeWidth={1.8} />
          Marcar provisionado
        </button>
      )}

      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal>
          <Dialog.Overlay
            className={cn(
              'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            )}
          />
          <Dialog.Content
            className={cn(
              'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
              'w-[min(520px,calc(100vw-32px))] max-h-[90dvh] overflow-y-auto',
              'rounded-[14px] border border-line bg-panel p-6 shadow-lg focus:outline-none',
            )}
          >
            <div className="mb-4 flex items-start justify-between gap-4">
              <Dialog.Title className="text-[16px] font-semibold tracking-[-0.005em] text-ink">
                Marcar como provisionado
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  type="button"
                  aria-label="Fechar"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
                >
                  <X size={14} strokeWidth={2} />
                </button>
              </Dialog.Close>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-3" noValidate>
              <Field label="External tenant ID" required error={errors.external_tenant_id}>
                {({ inputId }) => (
                  <Input
                    id={inputId}
                    value={form.external_tenant_id}
                    onChange={(e) => setForm((p) => ({ ...p, external_tenant_id: e.target.value }))}
                    placeholder="tenant_abc123"
                    hasError={Boolean(errors.external_tenant_id)}
                  />
                )}
              </Field>
              <Field label="External user ID (opcional)" error={errors.external_user_id}>
                {({ inputId }) => (
                  <Input
                    id={inputId}
                    value={form.external_user_id}
                    onChange={(e) => setForm((p) => ({ ...p, external_user_id: e.target.value }))}
                    placeholder="usr_xyz"
                  />
                )}
              </Field>
              <Field label="URL de acesso (opcional)" error={errors.access_url}>
                {({ inputId }) => (
                  <Input
                    id={inputId}
                    type="url"
                    value={form.access_url}
                    onChange={(e) => setForm((p) => ({ ...p, access_url: e.target.value }))}
                    placeholder="https://app.exemplo.com/login"
                    hasError={Boolean(errors.access_url)}
                  />
                )}
              </Field>
              <Field label="Notas (opcional)" error={errors.notes}>
                {({ inputId }) => (
                  <Textarea
                    id={inputId}
                    rows={2}
                    value={form.notes}
                    onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                  />
                )}
              </Field>
              <div className="mt-2 flex items-center justify-end gap-2">
                <PillButton type="button" onClick={() => setOpen(false)} disabled={mut.isPending}>
                  Cancelar
                </PillButton>
                <PillButton type="submit" solid disabled={mut.isPending}>
                  {mut.isPending ? 'Salvando…' : 'Confirmar'}
                </PillButton>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
